import { AxiosError } from 'axios';
import { getLogisticServiceConfigurationSummary, getShipmentConfigurationSummary } from '../services/product-configuration-summary.service';
import {
  IConfigurationTransitionBlockedApiError,
  IProductConfigurationSummaryProduct,
  IProductConfigurationTransitionBlocker,
  IProductConfigurationTransitionReadiness,
  PRODUCT_CONFIGURATION_TRANSITION
} from '../types/product-configuration-summary.types';

export const LOGISTIC_SERVICE_ACTIVE_BLOCK_MESSAGE =
  'No puedes mover el servicio a activo hasta completar las configuraciones y que estas sean confirmadas.';

export const SHIPMENT_TRANSIT_TO_DESTINATION_BLOCK_MESSAGE =
  'No puedes mover la orden a Tránsito a Destino hasta completar las configuraciones y que estas sean confirmadas.';

export const GO_TO_CONFIGURATIONS_BUTTON_TEXT = 'Ir a las configuraciones';

/**
 * Indica si la transición puede continuar según la evaluación del backend.
 * @param readiness - Resultado de transitionReadiness del summary
 */
export const canProceedWithTransition = (
  readiness?: IProductConfigurationTransitionReadiness | null
): boolean => {
  if (!readiness?.applies) return true;
  return readiness.allowed ?? readiness.canUpdateStatus ?? false;
};

/**
 * Muestra la notificación de bloqueo por configuraciones con el copy definido en el requerimiento.
 * @param displayErrorNotification - Función de notificación del proyecto
 * @param mainMessage - Mensaje principal en español
 * @param button1Text - Texto del botón opcional
 * @param button1Action - Acción del botón opcional
 */
export const displayConfigurationTransitionBlockedNotification = ({
  displayErrorNotification,
  mainMessage,
  button1Text,
  button1Action
}: {
  displayErrorNotification: (params?: {
    customJSXMessage?: JSX.Element;
    button1Text?: string;
    button1Action?: () => void;
  }) => void;
  mainMessage: string;
  button1Text?: string;
  button1Action?: () => void;
}): void => {
  displayErrorNotification({
    customJSXMessage: <>{mainMessage}</>,
    button1Text,
    button1Action
  });
};

/**
 * Extrae blockers de un error 400 del API de cambio de estado.
 * @param error - Error de Axios
 */
export const parseConfigurationBlockersFromApiError = (
  error: unknown
): IConfigurationTransitionBlockedApiError | null => {
  const axiosError = error as AxiosError<IConfigurationTransitionBlockedApiError>;
  const responseData = axiosError?.response?.data;

  if (!responseData?.blockers?.length) return null;

  return responseData;
};

/**
 * Evalúa si un error de API corresponde a bloqueo por configuraciones.
 * @param error - Error de Axios
 */
export const isConfigurationTransitionBlockedError = (error: unknown): boolean => {
  return !!parseConfigurationBlockersFromApiError(error)?.blockers?.length;
};

/**
 * Evalúa el summary de SL para transición a Activo y retorna si está bloqueado.
 * @param logisticServiceId - ID del servicio logístico
 */
export const evaluateLogisticServiceActiveTransition = async (
  logisticServiceId: number | string
): Promise<{
  isBlocked: boolean;
  blockers: IProductConfigurationTransitionBlocker[];
  products?: IProductConfigurationSummaryProduct[];
}> => {
  const summary = await getLogisticServiceConfigurationSummary(
    logisticServiceId,
    PRODUCT_CONFIGURATION_TRANSITION.LOGISTIC_SERVICE_ACTIVE
  );

  const readiness = summary.transitionReadiness;

  if (canProceedWithTransition(readiness)) {
    return { isBlocked: false, blockers: [] };
  }

  return {
    isBlocked: true,
    blockers: readiness?.blockers ?? [],
    products: summary.products
  };
};

/**
 * Evalúa el summary de envío para transición a Tránsito al destino.
 * @param shipmentId - ID del envío
 */
export const evaluateShipmentTransitToDestinationTransition = async (
  shipmentId: number | string
): Promise<{
  isBlocked: boolean;
  blockers: IProductConfigurationTransitionBlocker[];
  products?: IProductConfigurationSummaryProduct[];
}> => {
  const summary = await getShipmentConfigurationSummary(
    shipmentId,
    PRODUCT_CONFIGURATION_TRANSITION.SHIPMENT_TRANSIT_TO_DESTINATION
  );

  const readiness = summary.transitionReadiness;

  if (canProceedWithTransition(readiness)) {
    return { isBlocked: false, blockers: [] };
  }

  return {
    isBlocked: true,
    blockers: readiness?.blockers ?? [],
    products: summary.products
  };
};

/**
 * Maneja errores 400 de bloqueo por configuraciones al mutar estado.
 * @param error - Error capturado
 * @param mainMessage - Mensaje principal en español
 * @param displayErrorNotification - Función de notificación
 */
export const handleConfigurationTransitionBlockedApiError = (
  error: unknown,
  mainMessage: string,
  displayErrorNotification: (params?: {
    customJSXMessage?: JSX.Element;
    button1Text?: string;
    button1Action?: () => void;
  }) => void,
  button1Text?: string,
  button1Action?: () => void
): boolean => {
  const blockedError = parseConfigurationBlockersFromApiError(error);

  if (!blockedError?.blockers?.length) return false;

  displayConfigurationTransitionBlockedNotification({
    displayErrorNotification,
    mainMessage,
    button1Text,
    button1Action
  });

  return true;
};
